import Commons from '../Services/Common/Common';
import { apiRequest } from '../Services/Common/ApiRequest';
import Global from '../Services/Common/Global';
import { COM_BASIC_Schema } from '../Scripts/BasicInfo/Company/COM_BASIC/COM_BASIC_Schema';
import { PLA_BASIC_Schema } from '../Scripts/BasicInfo/Plant/PLA_BASIC/PLA_BASIC_Schema';
import { mapParamsBySchema } from '../util';
import { handleQuerySuccess } from './useQuriesSuccess';

export const IsAdminUser = (userInfo) => userInfo?.authCd === 'ADMIN';

/** ADMIN 검색 콤보용 API 캐시 TTL (ms) */
const ADMIN_SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;

const companyMasterCache = {
  userId: '',
  rows: null,
  fetchedAt: 0,
  inflight: null,
};

const adminAllPlantsCache = {
  userId: '',
  rows: null,
  fetchedAt: 0,
  inflight: null,
};

const isCacheFresh = (fetchedAt) =>
  fetchedAt > 0 && Date.now() - fetchedAt < ADMIN_SEARCH_CACHE_TTL_MS;

export const InvalidateAdminSearchCaches = () => {
  companyMasterCache.rows = null;
  companyMasterCache.fetchedAt = 0;
  companyMasterCache.inflight = null;
  adminAllPlantsCache.rows = null;
  adminAllPlantsCache.fetchedAt = 0;
  adminAllPlantsCache.inflight = null;
};

export const NormalizeCustRow = (item) => ({
  coCd: item?.coCd ?? item?.CoCd ?? '',
  custCd: item?.custCd ?? item?.CustCd ?? item?.CUST_CD ?? '',
  custNm:
    item?.custNm ??
    item?.CustNm ??
    item?.CUST_NM ??
    item?.custCd ??
    item?.CustCd ??
    '',
});

const SitesFromCompanyInfoRows = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return [];
  const first = list[0];
  if (Array.isArray(first?.custList)) {
    return first.custList.map(NormalizeCustRow);
  }
  if (first?.custCd != null || first?.custNm != null) {
    return list.map(NormalizeCustRow);
  }
  return [];
};

const AttachCoCdToCustRows = (rows, coCd) =>
  (rows || []).map((item) => {
    const normalized = NormalizeCustRow(item);
    return { ...normalized, coCd: normalized.coCd || coCd || '' };
  });

const FilterCustRowsByCoCd = (rows, coCd) => {
  const targetCoCd = String(coCd || '').trim();
  if (!targetCoCd) return rows || [];
  return (rows || []).filter((item) => {
    const rowCoCd = String(item.coCd || '').trim();
    return !rowCoCd || rowCoCd === targetCoCd;
  });
};

const FetchAdminAllPlantRows = async (userInfo, { force = false } = {}) => {
  const userId = userInfo?.userid || '';
  if (!userId) return [];

  if (
    !force &&
    adminAllPlantsCache.userId === userId &&
    Array.isArray(adminAllPlantsCache.rows) &&
    isCacheFresh(adminAllPlantsCache.fetchedAt)
  ) {
    return adminAllPlantsCache.rows;
  }

  if (!force && adminAllPlantsCache.inflight) {
    return adminAllPlantsCache.inflight;
  }

  adminAllPlantsCache.inflight = (async () => {
    try {
      const params = mapParamsBySchema(PLA_BASIC_Schema.mapper.search1, {
        userInfo,
      });
      const result = await apiRequest(
        'post',
        Global.urls.Plant.getPlaBasicR1,
        params
      );
      const rows = await handleQuerySuccess(result, {});
      const list = Array.isArray(rows) ? rows : [];
      adminAllPlantsCache.userId = userId;
      adminAllPlantsCache.rows = list;
      adminAllPlantsCache.fetchedAt = Date.now();
      return list;
    } finally {
      adminAllPlantsCache.inflight = null;
    }
  })();

  return adminAllPlantsCache.inflight;
};

/** 회사(CoCd)별 사업장 목록 — ADMIN은 사업장 전체 1회 조회 후 캐시·필터 */
export const FetchCustRowsByCoCd = async (
  coCd,
  userInfo = null,
  options = {}
) => {
  if (!coCd) return [];
  const { force = false } = options || {};
  try {
    if (IsAdminUser(userInfo)) {
      const rows = await FetchAdminAllPlantRows(userInfo, { force });
      return FilterCustRowsByCoCd(AttachCoCdToCustRows(rows, coCd), coCd);
    }

    const encryptedParams = mapParamsBySchema(
      {
        CoCd: (_, context) =>
          Commons.EncryptAES(context.search?.coCd || '') || '',
      },
      { search: { coCd } }
    );
    const result = await apiRequest(
      'post',
      Global.urls.Utility.getCompanyInfo,
      encryptedParams
    );
    return AttachCoCdToCustRows(
      SitesFromCompanyInfoRows(result?.data ?? result ?? []),
      coCd
    );
  } catch (e) {
    console.error('[FetchCustRowsByCoCd]', e?.message || e);
    return [];
  }
};

export const MapCustRowsToCustList = (rows, coCd) =>
  FilterCustRowsByCoCd(AttachCoCdToCustRows(rows, coCd), coCd)
    .map((item) => ({ value: item.custCd, name: item.custNm }))
    .filter((item) => item.value);

/** GetComBasicR1 행 → 콤보/폼용 회사 목록 */
export const MapCompanyRowsToCoList = (rows) =>
  (rows || [])
    .map((c) => ({
      value: String(c.coCd ?? c.CoCd ?? '').trim(),
      name: String(c.coNm ?? c.CoNm ?? '').trim(),
      coCd: String(c.coCd ?? c.CoCd ?? '').trim(),
      coNm: String(c.coNm ?? c.CoNm ?? '').trim(),
      ceoName: String(c.ceoNm ?? c.CeoNm ?? '').trim(),
    }))
    .filter((c) => c.value);

export const NormalizeCompanyInfoRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((c) => ({
    ...c,
    coCd: c.coCd ?? c.CoCd ?? '',
    coNm: c.coNm ?? c.CoNm ?? '',
    ceoNm: c.ceoNm ?? c.CeoNm ?? '',
  }));

/** ADMIN 전용 — 회사 마스터 최신 목록 조회 (캐시·동시 요청 병합) */
export const FetchAdminCompanyMasterRows = async (
  userInfo,
  options = {}
) => {
  const { force = false } = options || {};
  if (!IsAdminUser(userInfo) || !userInfo?.userid) return [];

  const userId = userInfo.userid;
  if (
    !force &&
    companyMasterCache.userId === userId &&
    Array.isArray(companyMasterCache.rows) &&
    isCacheFresh(companyMasterCache.fetchedAt)
  ) {
    return companyMasterCache.rows;
  }

  if (!force && companyMasterCache.inflight) {
    return companyMasterCache.inflight;
  }

  companyMasterCache.inflight = (async () => {
    try {
      const params = mapParamsBySchema(COM_BASIC_Schema.mapper.search1, {
        userInfo,
      });
      const response = await apiRequest(
        'post',
        Global.urls.Company.getComBasicR1,
        params
      );
      if (
        response?.isSuccess === false ||
        response?.IsSuccess === false
      ) {
        return companyMasterCache.rows || [];
      }
      let rows = [];
      await handleQuerySuccess(response, {
        onSuccess: (list) => {
          rows = NormalizeCompanyInfoRows(list);
        },
      });
      companyMasterCache.userId = userId;
      companyMasterCache.rows = rows;
      companyMasterCache.fetchedAt = Date.now();
      return rows;
    } catch (e) {
      console.error('[FetchAdminCompanyMasterRows]', e?.message || e);
      return companyMasterCache.rows || [];
    } finally {
      companyMasterCache.inflight = null;
    }
  })();

  return companyMasterCache.inflight;
};
